import { marked, type MarkedOptions } from 'marked';
import DOMPurify from 'dompurify';

/**
 * Convertit le contenu HTML d'une note en texte brut
 * Remplace les balises HTML par leur équivalent texte
 */
export const htmlToPlainText = (html: string): string => {
    // Créer un élément temporaire pour parser le HTML
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;

    // Remplacer les balises de saut de ligne par des retours à la ligne
    tempElement.querySelectorAll('br').forEach((br) => {
        br.replaceWith('\n');
    });

    // Remplacer les paragraphes par des retours à la ligne
    tempElement.querySelectorAll('p').forEach((p) => {
        p.insertAdjacentText('afterend', '\n');
    });

    // Remplacer les listes par des puces
    tempElement.querySelectorAll('li').forEach((li) => {
        li.insertAdjacentText('beforebegin', '• ');
        li.insertAdjacentText('afterend', '\n');
    });

    // Remplacer les titres par du texte en majuscules
    tempElement.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
        const text = heading.textContent?.toUpperCase() || '';
        heading.replaceWith(`${text}\n`);
    });

    // Obtenir le texte brut et nettoyer les espaces
    let plainText = tempElement.textContent || tempElement.innerText || '';

    // Nettoyer les espaces multiples et les retours à la ligne excessifs
    plainText = plainText
        .replace(/\n\s*\n\s*\n/g, '\n\n') // Remplacer 3+ retours à la ligne par 2
        .replace(/[ \t]+/g, ' ') // Remplacer les espaces multiples par un seul
        .trim(); // Supprimer les espaces en début/fin

    return plainText;
};

/**
 * Nettoie un texte Markdown pour ne garder que du texte brut
 */
export const markDownToPlainText = (input: string): string => {
    let output = input;

    // 1. Gras : **texte** ou __texte__
    output = output.replace(/\*\*(.*?)\*\*/g, '$1');
    output = output.replace(/__(.*?)__/g, '$1');

    // 2. Italique : *texte* ou _texte_
    output = output.replace(/\*(.*?)\*/g, '$1');
    output = output.replace(/_(.*?)_/g, '$1');

    // 3. Inline code : `code`
    output = output.replace(/`([^`]+)`/g, '$1');

    // 4. Titres : # titre
    output = output.replace(/^#{1,6}\s*/gm, '');

    // 5. Listes : - item ou * item → normaliser en "- item"
    output = output.replace(/^\s*[-*+]\s+/gm, '- ');

    // 6. Citations : > citation
    output = output.replace(/^\s*>\s?/gm, '');

    // 7. Liens : [texte](url) → texte
    output = output.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

    return output;
};

export const isHtml = (content: string) => {
    const a = document.createElement('div');
    a.innerHTML = content;

    for (let c = a.childNodes, i = c.length; i--; ) {
        if (c[i].nodeType === 1) return true;
    }

    return false;
};

export const markDownToHtml = ({
    content,
    markedOptions,
}: {
    content: string;
    markedOptions?: MarkedOptions<string, string>;
}): string => {
    const markedParameter = {
        breaks: true, // Respecte les sauts de ligne
        gfm: true, // GitHub Flavored Markdown
        async: false, // Forcer le mode synchrone
        renderer: new marked.Renderer(),
        ...markedOptions,
    };

    // Configuration sécurisée de Marked
    marked.setOptions(markedParameter);

    const html = marked.parse(content) as string;

    // Sanitize le HTML généré
    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
            'p',
            'br',
            'strong',
            'b',
            'em',
            'i',
            'u',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'ul',
            'ol',
            'li',
            'blockquote',
            'code',
            'pre',
            'a',
        ],
        ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
        ADD_ATTR: ['target', 'rel'],
        FORBID_CONTENTS: ['script'],
    });
};

/**
 * Convertit du texte brut en HTML pour l'envoi par mail
 * Échappe les entités HTML (&, <, >, ") puis remplace les sauts de ligne par des balises <br>
 */
export const plainTextToHtml = (text: string): string =>
    text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/\r\n|\r|\n/g, '<br>');

/**
 * Formate le contenu de la note pour la copie
 * Ajoute des métadonnées utiles
 */
export const formatNoteForCopy = (
    content: string,
    createdBy: string,
    createdAt: Date,
    isImportant?: boolean,
): string => {
    const plainContent = htmlToPlainText(content);
    const dateStr = createdAt.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

    let formattedContent = plainContent;

    // Ajouter un en-tête si la note est importante
    if (isImportant) {
        formattedContent = `⚠️ NOTE IMPORTANTE\n\n${formattedContent}`;
    }

    // Ajouter les métadonnées en bas
    formattedContent += `\n\n---\nNote de ${createdBy}\n${dateStr}`;

    return formattedContent;
};
